import React, { useEffect, useState } from "react";
import { Box, Divider, Stack, TextField, Typography } from "@mui/material";
import { Refresh } from "@mui/icons-material";
import { MuiTelInput } from "mui-tel-input";

import BmAlert from "../global-components/BM_Alert";
import { MainButton, SecondaryButton } from "../global-components/MainButton";

// --- Context ---
import { useBooking } from "../bookingContext/BookingContex";

// --- Services ---
import {
  NormalizeEmail,
  NormalizePhone,
  ValidateEmail,
  ValidatePhone,
  ValidateLegalName,
  FormatName,
  isSLMobile,
  isSLLandline,
  isInternational,
  validateRegisteredSLUser,
  validateRegisteredInternationalUser,
  validateRegisteredUser,
} from "../services/validationService";

import { sendOTP, verifyOTP } from "../services/OTPservice";
import { signInWithOTP, getUser } from "auth/authenticate";

// --- Step 1 Verification Component ---
const Step1Verification = () => {
  const {
    bookingPerson,
    setBookingPerson,
    contactDetails,
    setContactDetails,
    updateContactAfterVerification,
    markAsRegistered,
    markAsAuthenticated,
    markAsGuest,
    updateName,
    nextStep,
    isGuest,
    isRegistered,
    isAuthenticated,
    onAuthenticationSuccess,
  } = useBooking();

  // --- Local States ---
  const [page, setPage] = useState(0); // 0=contact, 1=confirm, 2=otp, 3=name
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [fname, setFname] = useState(bookingPerson.name.firstName || "");
  const [lname, setLname] = useState(bookingPerson.name.lastName || "");
  const [lastOtpSentTime, setLastOtpSentTime] = useState(0);

  // --- Derived Contact ---
  const phone = contactDetails.enteredPhone;
  const email = contactDetails.enteredEmail;

  // --- 1. Contact Submission ---
  const handleContactSubmit = async () => {
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      const normalizedPhone = NormalizePhone(phone);
      const validatedPhone = ValidatePhone(normalizedPhone);
      const normalizedEmail = NormalizeEmail(email);
      const validatedEmail = ValidateEmail(normalizedEmail);

      if (validatedPhone !== true || validatedEmail !== true) {
        if (validatedPhone !== true) setError(validatedPhone);
        else if (validatedEmail !== true) setError(validatedEmail);
        setLoading(false);
        return;
      }

      // Save normalized contact details
      setContactDetails({
        enteredEmail: normalizedEmail,
        enteredPhone: normalizedPhone,
        verifiedEmail: "",
        verifiedPhone: "",
      });

      // Validate against backend
      const contactsRegistered = await validateRegisteredUser(
        normalizedEmail,
        normalizedPhone
      );
      // console.log("Contact validation result:", contactsRegistered);

      if (contactsRegistered?.isExisting === true) {
        // ❗️ Show decision screen instead of going directly to OTP
        setSuccess(null);
        setPage(0.5); // new “account found” screen
      } else {
        // Guest path
        markAsGuest();
        setSuccess("New user detected. Proceeding as guest.");
        setPage(1);
      }
    } catch (err) {
      console.error("Error during contact submission:", err);
      setError(err.message || "Failed to validate contact details.");
    } finally {
      setLoading(false);
    }
  };

  // --- 2. Send OTP ---
  const handleSendOtp = async (isResend = false) => {
    const loaderSetter = isResend ? setResendLoading : setLoading;
    loaderSetter(true);
    setError(null);
    setSuccess(null);

    if (isResend && Date.now() - lastOtpSentTime < 30000) {
      const wait = Math.ceil((30000 - (Date.now() - lastOtpSentTime)) / 1000);
      setError(`Please wait ${wait}s before requesting another code.`);
      loaderSetter(false);
      return;
    }

    try {
      await sendOTP(phone, email);

      setLastOtpSentTime(Date.now());
      setSuccess("Verification code sent successfully.");
      if (!isResend) setPage(2);
    } catch (err) {
      setError(err.message || "Failed to send verification code.");
    } finally {
      loaderSetter(false);
    }
  };

  // --- 3. Verify OTP ---
  const handleVerifyOtp = async () => {
    setError(null);
    setSuccess(null);
    setLoading(true);

    if (otp.length !== 4) {
      setError("Please enter a valid 4-digit code.");
      setLoading(false);
      return;
    }

    try {
      if (isRegistered) {
        // Login flow for registered but logged-out user
        const loginResponse = await signInWithOTP(phone, email, otp);
        // I think this isn't working here. let's see
        // alert("is Logged in? " + JSON.stringify(loginResponse));

  if (loginResponse?.success) {
    // Update global authentication status
    if (typeof onAuthenticationSuccess === "function") {
      onAuthenticationSuccess(loginResponse.user);
    }

    // update bookingPerson immediately
  setBookingPerson((prev) => ({
    ...prev,
    id: loginResponse.user.userId || prev.id,
    username: loginResponse.user.username ,
    // email : loginResponse.user.email,
    // name: {
    //   firstName: loginResponse.user.firstName || prev.name.firstName || "",
    //   lastName: loginResponse.user.lastName || prev.name.lastName || "",
    // },
    registered: true,
    verified: true,
    authStatus: "logged_in",
  }));

    // Update local booking context
    markAsAuthenticated();

    setSuccess("Logged in successfully!");
    // await new Promise((resolve) => setTimeout(resolve, 200));

    nextStep();
    return;
  }

        throw new Error("Invalid or expired OTP.");
      } else {
        // Guest verification flow
        const verified = await verifyOTP(
          phone,
          email,
          otp
          // isSLMobile(phone),
          // isSLLandline(phone)
        );
        if (verified) {
          updateContactAfterVerification(email, phone);
          setSuccess("Verification successful!");
          setTimeout(() => {
            setPage(3); // proceed to name input
          }, 1000);
        } else {
          throw new Error("Invalid OTP or code expired.");
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // --- 4. Guest Name Submission ---
  const handleNameSubmit = () => {
    setError(null);
    const fValid = ValidateLegalName(fname);
    const lValid = ValidateLegalName(lname);
    if (fValid !== true) return setError(`First name: ${fValid}`);
    if (lValid !== true) return setError(`Last name: ${lValid}`);

    updateName(FormatName(fname), FormatName(lname));
    nextStep();
  };

  // --- Decision Handlers for Existing Account ---
  const handleProceedAsRegistered = () => {
    markAsRegistered();
    setPage(1); // Go to confirmation to send OTP
  };

  const handleEditContactData = () => {
    markAsGuest(); // Reset to guest temporarily
    setPage(0); // Go back to contact form
  };

  // --- RENDERING LOGIC ---
  const renderPage = () => {
    switch (page) {
      case 0: // contact
        return (
          <Stack spacing={2}>
            <Typography variant="subtitle1">Enter Contact Details</Typography>
            <MuiTelInput
              label="Phone Number"
              defaultCountry="LK"
              value={phone}
              onChange={(newPhone) =>
                setContactDetails((prev) => ({
                  ...prev,
                  enteredPhone: newPhone,
                }))
              }
              error={!!error && error.toLowerCase().includes("phone")}
            />
            <TextField
              fullWidth
              label="Email Address"
              value={email}
              onChange={(e) =>
                setContactDetails((prev) => ({
                  ...prev,
                  enteredEmail: e.target.value,
                }))
              }
              error={!!error && error.toLowerCase().includes("email")}
            />
            {error && (
              <BmAlert severity="error" title="Error" message={error} />
            )}
            <Box display="flex" justifyContent="flex-end">
              <MainButton
                btnLabel="Continue"
                onClick={handleContactSubmit}
                loading={loading}
              />
            </Box>
          </Stack>
        );
      case 0.5: // Account found decision screen
        return (
          <Stack spacing={2}>
            <Typography variant="h6" color="warning.main">
              Existing Account Detected
            </Typography>

            <BmAlert
              severity="warning"
              title="Account Found by Contact Details"
              message="These contact details already belong to an existing user account. 
        Would you like to continue verification to log in, or change the contact details?"
            />

            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              pt={1}
              spacing={2}
            >
              {/* Secondary Button: go back and edit contact */}
              <SecondaryButton
                onClick={handleEditContactData}
                btnLabel={"No – Change Contact Details"}
                fullWidth={true}
                disabled={loading}
                variant="outlined"
              />

              {/* Main Button: proceed to OTP */}
              <MainButton
                onClick={handleProceedAsRegistered}
                btnLabel={"Yes – Continue Verification"}
                fullWidth={true}
                loading={loading}
              />
            </Stack>
          </Stack>
        );
      case 1: // confirmation
        return (
          <Stack spacing={2}>
            <Typography variant="subtitle1">
              Confirm your verification method
            </Typography>
            {isRegistered && (
              <BmAlert
                severity="warning"
                title="Existing Account Detected"
                message="These details belong to an existing user. Complete OTP verification to log in."
              />
            )}
            <BmAlert
              severity="info"
              title="Verification Info"
              message={`A verification code will be sent to ${phone} and ${email}`}
            />

            <Stack
              direction="row"
              justifyContent="space-between"
              spacing={2}
              pt={1}
            >
              <SecondaryButton
                btnLabel="Back"
                onClick={() => setPage(0)}
                disabled={loading}
              />
              <MainButton
                btnLabel="Send OTP"
                onClick={() => handleSendOtp(false)}
                loading={loading}
              />
            </Stack>
          </Stack>
        );

      case 2: // otp input
        return (
          <Stack spacing={2}>
            <Typography variant="subtitle1">Enter Verification Code</Typography>
            {success && (
              <BmAlert severity="success" title="Success" message={success} />
            )}
            {error && (
              <BmAlert severity="error" title="Error" message={error} />
            )}
            <TextField
              label="Enter 4-digit OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              inputProps={{ maxLength: 4 }}
            />
            <Stack direction="row" spacing={2}>
              <SecondaryButton
                icon={<Refresh />}
                btnLabel="Resend OTP"
                onClick={() => handleSendOtp(true)}
                loading={resendLoading}
              />
              <MainButton
                btnLabel="Verify"
                onClick={handleVerifyOtp}
                loading={loading}
              />
            </Stack>
          </Stack>
        );

      case 3: // guest name
        return (
          <Stack spacing={2}>
            <Typography variant="subtitle1">Tell us about yourself</Typography>
            {error && (
              <BmAlert severity="error" title="Error" message={error} />
            )}
            <TextField
              label="First Name"
              value={fname}
              onChange={(e) => setFname(e.target.value)}
            />
            <TextField
              label="Last Name"
              value={lname}
              onChange={(e) => setLname(e.target.value)}
            />
            <Box display="flex" justifyContent="flex-end">
              <MainButton
                btnLabel="Continue to Step 2"
                onClick={handleNameSubmit}
              />
            </Box>
          </Stack>
        );

      default:
        return (
          <BmAlert
            severity="warning"
            title="Error"
            message="Invalid verification step."
          />
        );
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Divider sx={{ mb: 2 }} />
      {renderPage()}
    </Box>
  );
};

export default Step1Verification;
